import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Paper,
  Grid,
  TextField,
  Container,
  Stack,
  Chip,
  alpha,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  InputAdornment,
  Tooltip
} from "@mui/material";
import InventoryIcon from "@mui/icons-material/Inventory";
import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import DeleteIcon from "@mui/icons-material/Delete";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ReceiptIcon from "@mui/icons-material/Receipt";
import DiscountIcon from "@mui/icons-material/LocalOffer";
import NotesIcon from "@mui/icons-material/Notes";
import InfoIcon from "@mui/icons-material/Info";
import AsyncSelect from 'react-select/async';
import { searchProducts, addProduct } from "../../product/services/productService";
import { addPurchaseInvoice } from "../services/purchaseService";
import { getSuppliers, searchSuppliersByName } from "../../suppliers/services/supplierService";
import SupplierSelect from "../../suppliers/components/SupplierSelect";
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

function AddPurchasePage() {
  const navigate = useNavigate();
  
  // Product selection state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isNewProduct, setIsNewProduct] = useState(false);
  const [newProductData, setNewProductData] = useState({ 
    Name: "", 
    SKU: "", 
    SalePrice: "", 
    PurchasePrice: "" 
  });
  const [quantity, setQuantity] = useState(1);
  const [unitPrice, setUnitPrice] = useState("");
  const [taxRate, setTaxRate] = useState(21); // Default 21% VAT
  
  // Invoice header state
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [discountAmount, setDiscountAmount] = useState(0);
  
  // Line items
  const [lineItems, setLineItems] = useState([]);
  
  // Auto-generated invoice number
  const [invoiceNumber, setInvoiceNumber] = useState(() => {
    const year = new Date().getFullYear();
    const timestamp = Date.now();
    return `PI-${year}-${String(timestamp % 10000).padStart(4, "0")}`;
  });

  // Flags
  const [updateInventory, setUpdateInventory] = useState(true);
  const [updateCostPrices, setUpdateCostPrices] = useState(true);

  // Suppliers
  const [suppliers, setSuppliers] = useState([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);

  // ✅ Auto-calculate totals
  const calculations = React.useMemo(() => {
    const subTotal = lineItems.reduce((sum, item) => sum + item.totalPrice, 0);
    const taxAmount = lineItems.reduce((sum, item) => {
      const itemTax = (item.totalPrice * item.taxRate) / 100;
      return sum + itemTax;
    }, 0);
    const totalAmount = subTotal + taxAmount - discountAmount;
    
    return {
      subTotal: subTotal.toFixed(2),
      taxAmount: taxAmount.toFixed(2),
      discountAmount: Number(discountAmount).toFixed(2),
      totalAmount: totalAmount.toFixed(2)
    };
  }, [lineItems, discountAmount]);

  // Fetch suppliers on mount
  useEffect(() => {
    const fetchSuppliers = async () => {
      setSuppliersLoading(true);
      try {
        const response = await getSuppliers();
        const suppliersData = response?.payload || response || [];
        setSuppliers(Array.isArray(suppliersData) ? suppliersData : []);
      } catch (error) {
        console.error('Error fetching suppliers:', error);
        toast.error('Failed to load suppliers');
        setSuppliers([]);
      } finally {
        setSuppliersLoading(false);
      }
    };
    fetchSuppliers();
  }, []);

  // Auto-set due date (30 days from invoice date)
  useEffect(() => {
    if (invoiceDate && !dueDate) {
      const invoice = new Date(invoiceDate);
      const due = new Date(invoice);
      due.setDate(due.getDate() + 30);
      setDueDate(due.toISOString().slice(0, 10));
    }
  }, [invoiceDate]);

  const loadProductOptions = async (inputValue) => {
    if (!inputValue || inputValue.length < 2) return [];
    let paramsObj;
    if (/^\d+$/.test(inputValue)) {
      paramsObj = { id: inputValue, sku: inputValue };
    } else {
      paramsObj = { name: inputValue, sku: inputValue };
    }
    const data = await searchProducts(paramsObj);
    const productsArray = Array.isArray(data?.payload) ? data.payload : Array.isArray(data) ? data : [];
    return productsArray.map((p) => ({
      value: p.Id,
      label: `${p.Name} (ID: ${p.Id})${p.SKU ? " - SKU: " + p.SKU : ""}`,
      ...p,
    }));
  };

  const loadSupplierOptions = async (inputValue) => {
    try {
      if (!inputValue || inputValue.trim().length < 3) return [];
      const response = await searchSuppliersByName(inputValue.trim());
      const searchResults = response?.payload || response || [];
      
      if (Array.isArray(searchResults)) {
        return searchResults.map(s => ({
          value: s.Id,
          label: s.Name,
          Name: s.Name,
          ...s
        }));
      }
      return [];
    } catch (error) {
      console.error('Error searching suppliers:', error);
      toast.error('Failed to search suppliers');
      return [];
    }
  };

  const handleProductSelect = (product) => {
    if (product && product.value === "new") {
      setIsNewProduct(true);
      setSelectedProduct(null);
      setUnitPrice("");
    } else {
      setIsNewProduct(false);
      setSelectedProduct(product);
      setUnitPrice(product?.CostPrice || product?.PurchasePrice || "");
    }
  };

  // ✅ Updated handleAddLineItem - merge products if already exists
  const handleAddLineItem = async () => {
    let productId = selectedProduct?.Id;
    let productName = selectedProduct?.Name || newProductData.Name;
    let sku = selectedProduct?.SKU || newProductData.SKU;

    if (isNewProduct) {
      try {
        const response = await addProduct(newProductData);
        const newProd = response?.payload || response;
        productId = newProd.Id;
        productName = newProd.Name;
        sku = newProd.SKU;
      } catch (error) {
        console.error('Error adding new product:', error);
        toast.error('Failed to add new product');
        return;
      }
    }

    if (!productId || !quantity || !unitPrice) {
      toast.error('Please fill all product fields');
      return;
    }

    // ✅ Check if product already exists in line items
    const existingItemIndex = lineItems.findIndex(item => item.productId === productId);

    if (existingItemIndex !== -1) {
      // ✅ Product exists - update quantity and recalculate
      setLineItems(prev => {
        const updated = [...prev];
        const existingItem = updated[existingItemIndex];
        const newQuantity = existingItem.quantity + Number(quantity);
        const newTotalPrice = newQuantity * Number(unitPrice);
        
        updated[existingItemIndex] = {
          ...existingItem,
          quantity: newQuantity,
          unitPrice: Number(unitPrice), // Update unit price in case it changed
          taxRate: Number(taxRate), // Update tax rate in case it changed
          totalPrice: newTotalPrice
        };
        
        return updated;
      });
      toast.success(`Quantity updated for ${productName}`);
    } else {
      // ✅ New product - add to line items
      const lineItem = {
        productId,
        productName,
        sku,
        quantity: Number(quantity),
        unitPrice: Number(unitPrice),
        taxRate: Number(taxRate),
        totalPrice: Number(quantity) * Number(unitPrice)
      };

      setLineItems(prev => [...prev, lineItem]);
      toast.success('Line item added');
    }

    // Reset product fields
    setSelectedProduct(null);
    setIsNewProduct(false);
    setNewProductData({ Name: "", SKU: "", SalePrice: "", PurchasePrice: "" });
    setQuantity(1);
    setUnitPrice("");
    setTaxRate(21);
  };

  const handleRemoveLineItem = (index) => {
    setLineItems(prev => prev.filter((_, i) => i !== index));
    toast.info('Line item removed');
  };

  const handleSubmitPurchaseInvoice = async () => {
    // Validation
    if (!selectedSupplier) {
      toast.error('Please select a supplier');
      return;
    }
    
    if (lineItems.length === 0) {
      toast.error('Please add at least one line item');
      return;
    }

    if (!invoiceDate) {
      toast.error('Please select an invoice date');
      return;
    }

    const purchaseData = {
      PurchaseInvoiceNumber: invoiceNumber,
      InvoiceDate: invoiceDate,
      DueDate: dueDate || null,
      SupplierId: selectedSupplier.value,
      Status: "Not Paid", // Default status
      SubTotal: parseFloat(calculations.subTotal),
      TaxAmount: parseFloat(calculations.taxAmount),
      DiscountAmount: parseFloat(calculations.discountAmount),
      TotalAmount: parseFloat(calculations.totalAmount),
      Notes: notes || null,
      ReferenceNumber: referenceNumber || null,
      UpdateInventory: updateInventory,
      UpdateCostPrices: updateCostPrices,
      LineItems: lineItems.map(item => ({
        ProductId: item.productId,
        Quantity: item.quantity,
        UnitPrice: item.unitPrice,
        TaxRate: item.taxRate,
        TotalPrice: item.totalPrice
      }))
    };

    try {
      console.log('📤 Submitting purchase invoice:', purchaseData);
      const response = await addPurchaseInvoice(purchaseData);
      console.log('✅ Purchase invoice created:', response);
      toast.success('Purchase invoice created successfully!');
      navigate('/purchases');
    } catch (error) {
      console.error('❌ Error creating purchase invoice:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to create purchase invoice');
      }
    }
  };

  return (
    <Box sx={{ background: 'linear-gradient(to bottom, #f8fafc 0%, #f1f5f9 100%)', minHeight: '100vh', pt: 3, pb: 8 }}>
      <Container maxWidth="lg">
        {/* Header Card */}
        <Card elevation={3} sx={{ borderRadius: '16px', overflow: 'hidden', mb: 3 }}>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ bgcolor: alpha("#1976d2", 0.15), p: 1.2, borderRadius: '12px', mr: 2 }}>
                    <ReceiptIcon sx={{ color: "#1976d2" }} fontSize="large" />
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight="bold" color="primary">
                      New Purchase Invoice
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Create and manage purchase orders
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label={invoiceNumber}
                  color="primary"
                  sx={{ fontWeight: 'bold', fontSize: '1em', borderRadius: '8px', px: 2 }}
                />
              </Box>
            }
            sx={{
              background: alpha("#1976d2", 0.08),
              borderBottom: `1px solid ${alpha("#1976d2", 0.15)}`
            }}
          />
          <CardContent>
            <Grid container spacing={3}>
              {/* Supplier Selection */}
              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: 2, borderRadius: '12px', border: `1px solid ${alpha("#1976d2", 0.2)}`, height: '100%' }}>
                  <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                    <PersonIcon sx={{ color: "#1976d2" }} />
                    <Typography variant="subtitle1" fontWeight="bold" color="primary">
                      Supplier Information *
                    </Typography>
                  </Stack>
                  <SupplierSelect
                    value={selectedSupplier}
                    onChange={setSelectedSupplier}
                    loadOptions={loadSupplierOptions}
                    isLoading={suppliersLoading}
                    placeholder="Type at least 3 characters to search..."
                  />
                </Paper>
              </Grid>

              {/* Invoice Details */}
              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: 2, borderRadius: '12px', border: `1px solid ${alpha("#1976d2", 0.2)}`, height: '100%' }}>
                  <Stack direction="row" alignments="center" spacing={1} mb={2}>
                    <CalendarTodayIcon sx={{ color: "#1976d2" }} />
                    <Typography variant="subtitle1" fontWeight="bold" color="primary">
                      Invoice Details
                    </Typography>
                  </Stack>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        label="Invoice Date *"
                        type="date"
                        value={invoiceDate}
                        onChange={e => setInvoiceDate(e.target.value)}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="Due Date"
                        type="date"
                        value={dueDate}
                        onChange={e => setDueDate(e.target.value)}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Reference Number (Supplier's Invoice)"
                        value={referenceNumber}
                        onChange={e => setReferenceNumber(e.target.value)}
                        fullWidth
                        size="small"
                        placeholder="e.g., SUP-2024-001"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Add Product Card */}
        <Card elevation={3} sx={{ borderRadius: '16px', overflow: 'hidden', mb: 3 }}>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <InventoryIcon sx={{ color: "#1976d2", mr: 1 }} />
                <Typography variant="h6" fontWeight="bold" color="primary">
                  Add Products
                </Typography>
              </Box>
            }
            sx={{
              background: alpha("#4caf50", 0.08),
              borderBottom: `1px solid ${alpha("#4caf50", 0.15)}`
            }}
          />
          <CardContent>
            <Grid container spacing={2} alignItems="flex-start">
              {/* ✅ Product Select - Full width on its own row */}
              <Grid item xs={12}>
                <AsyncSelect
                  cacheOptions
                  loadOptions={async (inputValue) => {
                    const options = await loadProductOptions(inputValue);
                    return [...options, { value: "new", label: "+ Add New Product" }];
                  }}
                  value={selectedProduct}
                  onChange={handleProductSelect}
                  placeholder="Search product by name, SKU, or ID..."
                  isClearable
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderRadius: '12px',
                      minHeight: '45px',
                      borderColor: alpha("#1976d2", 0.3),
                      '&:hover': {
                        borderColor: "#1976d2"
                      }
                    }),
                    menu: (base) => ({
                      ...base,
                      borderRadius: '12px',
                      zIndex: 9999
                    })
                  }}
                />
              </Grid>

              {/* ✅ Quantity, Unit Price, Tax Rate, Add Button - All in one row */}
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Quantity *"
                  type="number"
                  value={quantity}
                  onChange={e => setQuantity(Math.max(1, Number(e.target.value)))}
                  fullWidth
                  size="small"
                  inputProps={{ min: 1 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  label="Unit Price *"
                  type="number"
                  value={unitPrice}
                  onChange={e => setUnitPrice(e.target.value)}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">€</InputAdornment>,
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  label="Tax Rate (%)"
                  type="number"
                  value={taxRate}
                  onChange={e => setTaxRate(e.target.value)}
                  fullWidth
                  size="small"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<AddIcon />}
                  fullWidth
                  onClick={handleAddLineItem}
                  disabled={!selectedProduct || !quantity || !unitPrice}
                  sx={{
                    height: '40px',
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 'bold'
                  }}
                >
                  Add
                </Button>
              </Grid>
            </Grid>

            {isNewProduct && (
              <Box sx={{ mt: 3, p: 2, bgcolor: alpha("#ff9800", 0.05), borderRadius: 2, border: `1px dashed ${alpha("#ff9800", 0.3)}` }}>
                <Typography variant="subtitle2" fontWeight="bold" mb={2} color="#ff9800">
                  Add New Product
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Product Name *"
                      value={newProductData.Name}
                      onChange={e => setNewProductData({ ...newProductData, Name: e.target.value })}
                      fullWidth
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="SKU *"
                      value={newProductData.SKU}
                      onChange={e => setNewProductData({ ...newProductData, SKU: e.target.value })}
                      fullWidth
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Sale Price"
                      type="number"
                      value={newProductData.SalePrice}
                      onChange={e => setNewProductData({ ...newProductData, SalePrice: e.target.value })}
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Purchase Price"
                      type="number"
                      value={newProductData.PurchasePrice}
                      onChange={e => setNewProductData({ ...newProductData, PurchasePrice: e.target.value })}
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Line Items Table */}
        {lineItems.length > 0 && (
          <Card elevation={3} sx={{ borderRadius: '16px', overflow: 'hidden', mb: 3 }}>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <InventoryIcon sx={{ color: "#1976d2", mr: 1 }} />
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      Line Items
                    </Typography>
                    <Chip
                      label={`${lineItems.length} item${lineItems.length > 1 ? 's' : ''}`}
                      size="small"
                      color="primary"
                      sx={{ ml: 2 }}
                    />
                  </Box>
                </Box>
              }
              sx={{
                background: alpha("#1976d2", 0.08),
                borderBottom: `1px solid ${alpha("#1976d2", 0.15)}`
              }}
            />
            <CardContent sx={{ p: 0 }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha("#1976d2", 0.05) }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>SKU</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Qty</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Unit Price</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Tax %</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Subtotal</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lineItems.map((item, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>{item.productName}</TableCell>
                        <TableCell>{item.sku}</TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">€{item.unitPrice.toFixed(2)}</TableCell>
                        <TableCell align="right">{item.taxRate}%</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          €{item.totalPrice.toFixed(2)}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveLineItem(idx)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider />

              {/* Totals Section */}
              <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    {/* Notes and Discount */}
                    <Stack spacing={2}>
                      <TextField
                        label="Discount Amount"
                        type="number"
                        value={discountAmount}
                        onChange={e => setDiscountAmount(Math.max(0, Number(e.target.value)))}
                        fullWidth
                        size="small"
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><DiscountIcon fontSize="small" /></InputAdornment>,
                          endAdornment: <InputAdornment position="end">€</InputAdornment>,
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                      />
                      <TextField
                        label="Notes"
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                        fullWidth
                        multiline
                        rows={3}
                        size="small"
                        placeholder="Add any additional notes..."
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><NotesIcon fontSize="small" /></InputAdornment>,
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                      />
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 2, bgcolor: alpha("#1976d2", 0.05) }}>
                      <Stack spacing={1.5}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body1">Subtotal:</Typography>
                          <Typography variant="body1" fontWeight="medium">€{calculations.subTotal}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body1">Tax Amount:</Typography>
                          <Typography variant="body1" fontWeight="medium">€{calculations.taxAmount}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body1">Discount:</Typography>
                          <Typography variant="body1" fontWeight="medium" color="error.main">
                            -€{calculations.discountAmount}
                          </Typography>
                        </Box>
                        <Divider />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="h6" fontWeight="bold" color="primary">
                            Total Amount:
                          </Typography>
                          <Typography variant="h6" fontWeight="bold" color="primary">
                            €{calculations.totalAmount}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Settings and Submit */}
        <Card elevation={3} sx={{ borderRadius: '16px', overflow: 'hidden' }}>
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, bgcolor: alpha("#4caf50", 0.05) }}>
                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={updateInventory}
                            onChange={e => setUpdateInventory(e.target.checked)}
                            color="success"
                          />
                        }
                        label="Update Inventory Stock"
                      />
                      <Tooltip title="Automatically update product stock quantities">
                        <InfoIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
                      </Tooltip>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={updateCostPrices}
                            onChange={e => setUpdateCostPrices(e.target.checked)}
                            color="success"
                          />
                        }
                        label="Update Product Cost Prices"
                      />
                      <Tooltip title="Update product cost prices based on this purchase">
                        <InfoIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
                      </Tooltip>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate('/purchases')}
                    sx={{
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontWeight: 'bold',
                      px: 4
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    size="large"
                    onClick={handleSubmitPurchaseInvoice}
                    disabled={!selectedSupplier || lineItems.length === 0}
                    sx={{
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontWeight: 'bold',
                      px: 4
                    }}
                  >
                    Create Purchase Invoice
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}

export default AddPurchasePage;